<script>
import { getUserSessions, deleteUserSession } from '@/api/user.js';

export default {
    data() {
        return {
            sessions: [],
            isLoading: false,
        };
    },
    computed: {
        userEditData() {
            return this.$store.getters.USER_EDIT_DATA;
        },
    },
    async mounted() {
        await this.loadSessions();
    },
    watch: {
        'userEditData.id': {
            async handler(newUserId) {
                if (newUserId) {
                    await this.loadSessions();
                }
            },
        },
    },
    methods: {
        async loadSessions() {
            if (!this.userEditData?.id) return;

            this.isLoading = true;
            try {
                this.sessions = await getUserSessions(this.userEditData.id);
            } catch (error) {
                console.error('Failed to load sessions:', error);
                this.sessions = [];
            } finally {
                this.isLoading = false;
            }
        },
        async logoutSession(session) {
            this.$store.commit('SHOP_POPUP_NAME', 'confirmed');
            this.$store.commit(
                'CONFIRMED_POPUP_METHOD',
                this.confirmLogoutSession.bind(this, session)
            );
        },
        async confirmLogoutSession(session) {
            const success = await deleteUserSession(
                this.userEditData.id,
                session.session_id
            );

            if (success) {
                this.$store.commit('POPUP_ADD', {
                    title: 'SESSION TERMINATED',
                    description: ['Session has been successfully terminated'],
                    color: '#00d25b',
                });

                // Refresh sessions list
                await this.loadSessions();
            }
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            // Parse as UTC and convert to Minsk timezone (UTC+3)
            const date = new Date(dateString + 'Z'); // Ensure it's treated as UTC
            return date.toLocaleString('en-US', {
                timeZone: 'Europe/Minsk',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        },
    },
};
</script>

<template>
    <div class="left_sub_panel_data">
        <div class="section_header">
            <button
                class="button_blue_100"
                @click="loadSessions"
                :disabled="isLoading"
            >
                Refresh
            </button>
        </div>

        <div v-if="isLoading" class="loading_container">
            <p>Loading sessions...</p>
        </div>

        <div v-else-if="sessions.length === 0" class="no_sessions">
            <p>No active sessions found</p>
        </div>

        <div v-else class="sessions_table_wrapper">
            <table class="sessions_table">
                <thead>
                    <tr>
                        <th>Session ID</th>
                        <th>Created</th>
                        <th>Last Activity</th>
                        <th>IP Address</th>
                        <th>Device : Browser</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="session in sessions"
                        :key="session.session_id"
                        :class="{ current_session: session.is_current }"
                    >
                        <td class="session_id_cell">
                            <span class="session_id_text">{{
                                session.session_id
                            }}</span>
                            <span
                                v-if="session.is_current"
                                class="current_badge"
                                >CURRENT</span
                            >
                        </td>
                        <td>{{ formatDate(session.created_at) }}</td>
                        <td>{{ formatDate(session.last_activity) }}</td>
                        <td>{{ session.ip_address || 'N/A' }}</td>
                        <td class="user_agent_cell">
                            {{ session.device_type || 'N/A' }} :
                            {{ session.browser || 'N/A' }}
                        </td>
                        <td class="actions_cell">
                            <button
                                class="button_blue_100 button_red"
                                @click="logoutSession(session)"
                                :disabled="session.is_current"
                            >
                                {{ session.is_current ? 'Current' : 'Logout' }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
.user_edit_section {
    padding: 20px;
}

.section_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.section_header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-color);
}

.loading_container,
.no_sessions {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
}

.sessions_table_wrapper {
    max-height: 400px;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--background);
}

.sessions_table {
    width: 100%;
    border-collapse: collapse;
}

.sessions_table thead {
    position: sticky;
    top: 0;
    background: var(--background);
    z-index: 1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.sessions_table th {
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
}

.sessions_table td {
    padding: 12px 15px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color-primary);
    background: var(--background);
}

.sessions_table tbody tr:hover td {
    background: var(--background-hover);
    transition: background 0.2s ease;
}

.sessions_table tbody tr.current_session td {
    background: linear-gradient(
        90deg,
        rgba(100, 150, 200, 0.15),
        rgba(100, 150, 200, 0.08)
    );
    border-left: 3px solid rgba(100, 150, 200, 0.6);
}

.sessions_table tbody tr.current_session:hover td {
    background: linear-gradient(
        90deg,
        rgba(100, 150, 200, 0.22),
        rgba(100, 150, 200, 0.12)
    );
}

.session_id_cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.session_id_text {
    font-family: monospace;
    font-size: 12px;
    color: var(--text-color-secondary);
}

.current_badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.user_agent_cell {
    max-width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: var(--text-color-secondary);
}

.actions_cell {
    text-align: center;
    white-space: nowrap;
}

.actions_cell button {
    min-width: 80px;
}

.actions_cell button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
